import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { marketingService } from '../../../services/marketingService';

const AutomatedReminders = ({ expanded = false }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    try {
      setLoading(true);
      const data = await marketingService?.getReminders();
      setReminders(data);
    } catch (err) {
      setError(err?.message || 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const reminderStats = {
    scheduled: reminders?.filter(r => r?.status === 'scheduled')?.length || 0,
    sent: reminders?.filter(r => r?.status === 'sent')?.length || 0,
    delivered: reminders?.filter(r => r?.status === 'delivered')?.length || 0,
    failed: reminders?.filter(r => r?.status === 'failed')?.length || 0
  };

  const channelBreakdown = [
    { name: 'SMS', icon: 'MessageSquare', count: reminders?.filter(r => r?.channel === 'sms')?.length || 0, color: 'text-primary' },
    { name: 'Email', icon: 'Mail', count: reminders?.filter(r => r?.channel === 'email')?.length || 0, color: 'text-success' },
    { name: 'Voice', icon: 'Phone', count: reminders?.filter(r => r?.channel === 'voice')?.length || 0, color: 'text-warning' }
  ];

  const displayReminders = expanded ? reminders : reminders?.slice(0, 5);

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Bell" size={20} className="text-primary" />
          Automated Reminders
        </h2>
        <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
          Configure
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-warning/10 rounded-lg p-3 border border-warning/20">
          <p className="text-xs text-warning mb-1">Scheduled</p>
          <p className="text-xl font-bold text-warning">{reminderStats?.scheduled}</p>
        </div>
        <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
          <p className="text-xs text-primary mb-1">Sent</p>
          <p className="text-xl font-bold text-primary">{reminderStats?.sent}</p>
        </div>
        <div className="bg-success/10 rounded-lg p-3 border border-success/20">
          <p className="text-xs text-success mb-1">Delivered</p>
          <p className="text-xl font-bold text-success">{reminderStats?.delivered}</p>
        </div>
        <div className="bg-error/10 rounded-lg p-3 border border-error/20">
          <p className="text-xs text-error mb-1">Failed</p>
          <p className="text-xl font-bold text-error">{reminderStats?.failed}</p>
        </div>
      </div>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Communication Channels</h3>
        <div className="grid grid-cols-3 gap-3">
          {channelBreakdown?.map((channel, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border text-center">
              <Icon name={channel?.icon} size={24} className={`mx-auto mb-2 ${channel?.color}`} />
              <p className="text-xs text-muted-foreground mb-1">{channel?.name}</p>
              <p className="text-lg font-bold text-foreground">{channel?.count}</p>
            </div>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error rounded-lg p-3">
          <p className="text-error text-sm">{error}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Upcoming Reminders</h3>
          {displayReminders?.map((reminder) => (
            <div key={reminder?.id} className="bg-muted/30 rounded-lg p-3 border border-border">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-semibold text-foreground text-sm">{reminder?.reminderType}</p>
                  <p className="text-xs text-muted-foreground">{reminder?.booking?.patient_name || 'Patient'}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    reminder?.status === 'delivered' ?'bg-success/10 text-success'
                      : reminder?.status === 'scheduled' ?'bg-warning/10 text-warning'
                      : reminder?.status === 'sent' ?'bg-primary/10 text-primary' :'bg-error/10 text-error'
                  }`}
                >
                  {reminder?.status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name={reminder?.channel === 'sms' ? 'MessageSquare' : reminder?.channel === 'email' ? 'Mail' : 'Phone'} size={12} />
                  {reminder?.channel?.toUpperCase()}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Clock" size={12} />
                  {new Date(reminder?.scheduledTime)?.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
          {!expanded && reminders?.length > 5 && (
            <Button variant="ghost" size="sm" fullWidth>
              View All Reminders ({reminders?.length})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AutomatedReminders;